import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  View,
  Text,
  ScrollView,
  TextInput,
  Pressable,
  useWindowDimensions,
  Platform,
  TouchableOpacity,
  ActivityIndicator,
  Modal,
  Alert,
  KeyboardAvoidingView,
} from 'react-native';
import Svg, { Path, Circle } from 'react-native-svg';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useTranslation } from 'react-i18next';
import PTLanguageToggle from '../PerfectTruck/PTLanguageToggle';
import { AdminColors } from '../Admin/constants/adminColors';
import { styles as baseStyles } from './RouteIssueSubmitScreen.styles';
import {
  fetchRouteRoadblocks,
  submitRouteRoadblock,
  parseError,
  RouteRoadblockResponseDTO,
  HarborImageUploadResult,
} from '../../api/deliveryRpmApi';
import { getLocalTimestamp } from '../../api/apiClient';
import HarborPhotoUpload from './components/HarborPhotoUpload';
import NotifySupervisorModal from './NotifySupervisorModal';
import { fetchEquipmentConfigItems } from '../PerfectTruck/perfectTruckApi';
import { EquipmentConfigItem } from '../Admin/pages/PerfectTruckManagement/types';
import { DeliveryDetail } from '../PerfectTruck/types';
import { useDarkStyles } from '../../theme/useDarkStyles';

// ─── Types ────────────────────────────────────────────────────────────────────

interface RouteIssueSubmitScreenProps {
  onNavigate: (screen: string, params?: Record<string, any>) => void;
  route?: {
    params?: {
      route?: string;
      date?: string;
      location?: string;
      deliveryDetail?: DeliveryDetail;
    };
  };
}

// ─── Constants ────────────────────────────────────────────────────────────────

const DETAILS_MAX_LEN = 1000;
const MISSING_EQUIPMENT_CODE = 'Missing Equipment';

// Default labels when pictureTitle / textFieldTitle is blank
const DEFAULT_PHOTO_LABEL = 'Photo';
const DEFAULT_TEXT_LABEL = 'Additional Details';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (iso: string) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
};

const UNICODE_EMOJI_REGEX = /\p{Extended_Pictographic}/u;

const hasUnicodeEmoji = (value: string) => UNICODE_EMOJI_REGEX.test(value);

// ─── Icons ────────────────────────────────────────────────────────────────────

const CheckIcon = () => (
  <Svg width={12} height={12} viewBox="0 0 24 24" fill="none">
    <Path d="M5 12l5 5L20 7" stroke="#fff" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const BoxIcon = () => (
  <Svg width={20} height={20} viewBox="0 0 24 24" fill="none">
    <Path d="M12 3L4 7v10l8 4 8-4V7l-8-4Z" stroke="#5C4EC9" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M4 7l8 4 8-4M12 11v10" stroke="#5C4EC9" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const WarnIcon = () => (
  <Svg width={20} height={20} viewBox="0 0 24 24" fill="none">
    <Path d="M12 3L2.5 20h19L12 3Z" stroke="#E5A408" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M12 9.5v4.5" stroke="#E5A408" strokeWidth={2} strokeLinecap="round" />
    <Circle cx={12} cy={17} r={0.4} fill="#E5A408" stroke="#E5A408" strokeWidth={2} />
  </Svg>
);

const ReasonCodeIcon: React.FC<{ iconName?: string }> = ({ iconName }) => {
  const styles = useDarkStyles(baseStyles);
  if (!iconName) {
    return <MaterialIcons name="help-outline" size={18} color={AdminColors.gray450} />;
  }
  if (hasUnicodeEmoji(iconName)) {
    return <Text style={styles.rcEmojiIcon}>{iconName}</Text>;
  }
  return <MaterialIcons name={iconName as any} size={18} color={AdminColors.blue700} />;
};

// ─── Main screen ──────────────────────────────────────────────────────────────

const RouteIssueSubmitScreen: React.FC<RouteIssueSubmitScreenProps> = ({ onNavigate, route: navRoute }) => {
  const styles = useDarkStyles(baseStyles);
  const { t, i18n } = useTranslation('harbor');
  const selectedLocation = useSelector((state: any) => state.appData?.selectedLocation);
  const params = navRoute?.params ?? {};
  const routeId = params.route ?? '';
  const routeDate = params.date ?? new Date().toISOString().slice(0, 10);
  const routeLocation = params.location ?? '';
  const deliveryDetail = params.deliveryDetail ?? null;
  const { width: viewportWidth } = useWindowDimensions();
  const isMobile = viewportWidth < 768;

  const reasonTileColumns = useMemo(() => {
    if (viewportWidth < 768) return 2;
    if (viewportWidth < 1280) return 4;
    return 6;
  }, [viewportWidth]);

  const reasonTileWidth = useMemo(() => {
    const pageHorizontalPadding = 48;
    const cardHorizontalPadding = 48;
    const gridGap = 12;
    const availableWidth = Math.max(viewportWidth - pageHorizontalPadding - cardHorizontalPadding, 280);
    return Math.floor((availableWidth - gridGap * (reasonTileColumns - 1)) / reasonTileColumns);
  }, [viewportWidth, reasonTileColumns]);

  // ── Load reason codes ────────────────────────────────────────────────────
  const [reasonCodes, setReasonCodes] = useState<RouteRoadblockResponseDTO[]>([]);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);

  // ── Equipment API state ───────────────────────────────────────────────────
  const [equipmentItems, setEquipmentItems] = useState<EquipmentConfigItem[]>([]);
  const [equipmentLoadError, setEquipmentLoadError] = useState(false);
  const [equipmentLoading, setEquipmentLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      // Include current i18n.language so backend returns labels in selected language
      const res = await fetchRouteRoadblocks({ roadblockTypes: 'Route Issue', isActive: 'true', size: '100', language: i18n.language });
      setReasonCodes(res.items);
    } catch (err) {
      setLoadError(parseError(err));
    } finally {
      setLoading(false);
    }
  }, [i18n.language]);

  // Store selected reason code ID (language-independent) to restore across language changes
  const savedReasonIdRef = useRef<number | null>(null);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Reason code ───────────────────────────────────────────────────────────
  const [selectedReasonId, setSelectedReasonId] = useState<number | null>(null);
  const [reasonError, setReasonError] = useState('');

  const selectedReason = useMemo(
    () => reasonCodes.find(r => r.id === selectedReasonId) ?? null,
    [reasonCodes, selectedReasonId],
  );

  // When a reason is selected, save its ID for restoration after language change
  useEffect(() => {
    if (selectedReasonId !== null) {
      savedReasonIdRef.current = selectedReasonId;
    }
  }, [selectedReasonId]);

  // When reasonCodes updates (e.g., from language change), restore previous selection by ID.
  // IDs stay constant across languages even though the displayed label is translated.
  useEffect(() => {
    if (savedReasonIdRef.current !== null && reasonCodes.length > 0) {
      const restored = reasonCodes.find(r => r.id === savedReasonIdRef.current);
      if (restored && restored.id !== selectedReasonId) {
        setSelectedReasonId(restored.id);
      }
    }
  }, [reasonCodes]);

  // Identify the "Missing Equipment" reason by its stable ID rather than its translated label —
  // `routeRoadblock` is localized per `i18n.language`, so a hardcoded English string match would
  // fail once the UI is switched to Spanish/French. We resolve the ID once via an English-language
  // lookup against the same config endpoint (roadblockType/id are language-independent).
  const [missingEquipmentReasonId, setMissingEquipmentReasonId] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchRouteRoadblocks({ roadblockTypes: 'Route Issue', isActive: 'true', size: '100', language: 'en' })
      .then(res => {
        if (cancelled) return;
        const found = res.items.find(
          r => r.routeRoadblock?.trim().toLowerCase() === MISSING_EQUIPMENT_CODE.toLowerCase(),
        );
        if (found) setMissingEquipmentReasonId(found.id);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const isMissingEquipment = selectedReasonId !== null && selectedReasonId === missingEquipmentReasonId;

  // ── Derived display config from selected reason ───────────────────────────
  const showPhoto = !!(selectedReason?.pictureRequired || selectedReason?.pictureOptional);
  const photoRequired = !!selectedReason?.pictureRequired;
  const photoLabel = selectedReason?.pictureTitle?.trim() || t('route_issue_submit.section_photo_attachment');

  const showTextField = !!(selectedReason?.textFieldRequired || selectedReason?.textFieldOptional);
  const textFieldRequired = !!selectedReason?.textFieldRequired;
  const textFieldLabel = selectedReason?.textFieldTitle?.trim() || t('route_issue_submit.section_additional_details');

  // ── Load equipment when Missing Equipment is selected ─────────────────────
  useEffect(() => {
    if (!isMissingEquipment) return;
    setEquipmentLoading(true);
    setEquipmentLoadError(false);
    fetchEquipmentConfigItems({ isActive: true, language: i18n.language })
      .then(items => setEquipmentItems(items))
      .catch(() => setEquipmentLoadError(true))
      .finally(() => setEquipmentLoading(false));
  }, [isMissingEquipment, i18n.language]);

  // ── Missing Equipment selection ───────────────────────────────────────────
  const [selectedEquipment, setSelectedEquipment] = useState<Set<string>>(new Set());
  const [equipmentError, setEquipmentError] = useState('');

  const toggleEquipment = (item: string) => {
    setSelectedEquipment(prev => {
      const next = new Set(prev);
      if (next.has(item)) next.delete(item); else next.add(item);
      return next;
    });
    setEquipmentError('');
  };

  // ── Additional details ────────────────────────────────────────────────────
  const [details, setDetails] = useState('');

  // ── Picture attachment ────────────────────────────────────────────────────
  const [pictureAttached, setPictureAttached] = useState(false);
  const [uploadResult, setUploadResult] = useState<HarborImageUploadResult | null>(null);

  const handlePhotoUploaded = (result: HarborImageUploadResult) => {
    setUploadResult(result);
    setPictureAttached(true);
  };

  // ── Submit / toast / error banner ────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [submitBannerError, setSubmitBannerError] = useState('');

  // ── Supervisor warning modal state ────────────────────────────────────────
  // Step 1: "Have you contacted your DDL?"
  const [supervisorWarningVisible, setSupervisorWarningVisible] = useState(false);
  // Step 2: "Are you sure you want to proceed?"
  const [supervisorConfirmVisible, setSupervisorConfirmVisible] = useState(false);

  // ── Supervisor automatic notification modal state ─────────────────────────
  const [supervisorNotifyVisible, setSupervisorNotifyVisible] = useState(false);
  // Holds the resolved submission payload while the notify modal is open
  const [pendingSubmitPayload, setPendingSubmitPayload] = useState<Parameters<typeof submitRouteRoadblock>[0] | null>(null);
  // Stores the submission ID returned from the API for the notification modal
  const [submittedSubmissionId, setSubmittedSubmissionId] = useState<number | null>(null);

  // ── Dirty state for cancel guard ──────────────────────────────────────────
  const isDirty = selectedReasonId !== null || selectedEquipment.size > 0 || details.length > 0 || pictureAttached;
  const [cancelConfirmVisible, setCancelConfirmVisible] = useState(false);

  // ── Validation ────────────────────────────────────────────────────────────
  const pictureOk = !photoRequired || pictureAttached;
  const textFieldOk = !textFieldRequired || details.trim().length > 0;

  const isSubmitEnabled = useMemo(() => {
    if (!selectedReasonId) return false;
    if (isMissingEquipment && selectedEquipment.size === 0) return false;
    if (isMissingEquipment && (equipmentLoadError || equipmentItems.length === 0)) return false;
    if (!pictureOk) return false;
    if (!textFieldOk) return false;
    return true;
  }, [selectedReasonId, isMissingEquipment, selectedEquipment, equipmentLoadError, equipmentItems.length, pictureOk, textFieldOk]);

  // ── Reason select ─────────────────────────────────────────────────────────
  const handleReasonSelect = (id: number) => {
    // Clear equipment selection when switching away from Missing Equipment
    if (isMissingEquipment && id !== missingEquipmentReasonId) {
      setSelectedEquipment(new Set());
      setEquipmentError('');
    }
    setSelectedReasonId(id);
    setReasonError('');
    // Reset picture + details when reason changes
    setUploadResult(null);
    setPictureAttached(false);
    setDetails('');
  };

  // ── Build submission payload ───────────────────────────────────────────────
  const buildPayload = (): Parameters<typeof submitRouteRoadblock>[0] => {
    const imageResponse = uploadResult
      ? JSON.stringify({ [uploadResult.originalFileName]: uploadResult.imageUrl })
      : undefined;
    return {
      reasonCodeId: selectedReasonId!,
      missingEquipment: isMissingEquipment && selectedEquipment.size > 0
        ? [...selectedEquipment].join(', ')
        : undefined,
      additionalDetails: details.trim() || undefined,
      imageResponse,
      routeId: routeId || undefined,
      location: selectedLocation?.name || routeLocation || '',
    };
  };

  // ── Core submit (performs the actual API call) ────────────────────────────
  const performSubmit = async (payload: Parameters<typeof submitRouteRoadblock>[0]) => {
    setSubmitting(true);
    setSubmitBannerError('');
    try {
      const response = await submitRouteRoadblock(payload);
      // Store submission ID if we're about to show notification modal
      if (selectedReason?.supervisorAutomaticNotification) {
        setSubmittedSubmissionId(response.submissionId);
      }
      onNavigate('RouteRoadblocks', { toastMessage: t('route_issue_submit.toast_success') });
    } catch (err) {
      const message = parseError(err);
      setSubmitBannerError(message || t('route_issue_submit.error_submit_generic'));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Submit entry point ────────────────────────────────────────────────────
  const handleSubmit = async () => {
    let valid = true;
    if (!selectedReasonId) {
      setReasonError(t('route_issue_submit.error_select_reason'));
      valid = false;
    }
    if (isMissingEquipment && selectedEquipment.size === 0) {
      setEquipmentError(t('route_issue_submit.error_select_equipment'));
      valid = false;
    }
    if (photoRequired && !pictureAttached) {
      Alert.alert(t('route_issue_submit.alert_photo_title'), t('route_issue_submit.alert_photo_required'));
      valid = false;
    }
    if (!valid) return;

    // Supervisor warning flow (step 1) takes precedence
    if (selectedReason?.supervisorNotificationWarning) {
      setSupervisorWarningVisible(true);
      return;
    }
    setSubmitting(true);
    setSubmitBannerError('');
    try {
      const imageResponse = uploadResult
        ? JSON.stringify({ [uploadResult.originalFileName]: uploadResult.imageUrl })
        : undefined;

      const response = await submitRouteRoadblock({
        reasonCodeId: selectedReasonId!,
        missingEquipment: isMissingEquipment && selectedEquipment.size > 0
          ? [...selectedEquipment].join(', ')
          : undefined,
        additionalDetails: details.trim() || undefined,
        imageResponse,
        routeId: routeId || undefined,
        location: selectedLocation?.name || routeLocation || '',
        language: i18n.language,
        clientTimestamp: getLocalTimestamp(),
        clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });

      // Check for automatic notification BEFORE navigating away
      if (selectedReason?.supervisorAutomaticNotification) {
        setSubmittedSubmissionId(response.submissionId);
        setPendingSubmitPayload(buildPayload());
        setSupervisorNotifyVisible(true);
      } else {
        // Navigate back; RouteRoadblocksScreen shows the success toast once mounted
        onNavigate('RouteRoadblocks', { toastMessage: t('route_issue_submit.toast_success') });
      }
    } catch (err) {
      const message = parseError(err);
      setSubmitBannerError(message || t('route_issue_submit.error_submit_generic'));
    } finally {
      setSubmitting(false);
    }

    const payload = buildPayload();

    // Supervisor automatic notification flow
    if (selectedReason?.supervisorAutomaticNotification) {
      setPendingSubmitPayload(payload);
      setSupervisorNotifyVisible(true);
      return;
    }

    await performSubmit(payload);
  };

  // ── Supervisor warning modal handlers ────────────────────────────────────
  const handleSupervisorWarningYes = () => {
    setSupervisorWarningVisible(false);
    const payload = buildPayload();
    if (selectedReason?.supervisorAutomaticNotification) {
      setPendingSubmitPayload(payload);
      setSupervisorNotifyVisible(true);
    } else {
      performSubmit(payload);
    }
  };

  const handleSupervisorWarningNo = () => {
    setSupervisorWarningVisible(false);
    setSupervisorConfirmVisible(true);
  };

  const handleSupervisorConfirmProceed = () => {
    setSupervisorConfirmVisible(false);
    const payload = buildPayload();
    if (selectedReason?.supervisorAutomaticNotification) {
      setPendingSubmitPayload(payload);
      setSupervisorNotifyVisible(true);
    } else {
      performSubmit(payload);
    }
  };

  const handleSupervisorConfirmBack = () => {
    setSupervisorConfirmVisible(false);
  };

  // ── Supervisor automatic notification handlers ────────────────────────────
  const handleSupervisorNotifyClose = () => {
    setSupervisorNotifyVisible(false);
    if (pendingSubmitPayload) {
      performSubmit(pendingSubmitPayload);
      setPendingSubmitPayload(null);
    }
    setSubmittedSubmissionId(null);
  };

  const handleSupervisorNotifyBack = () => {
    setSupervisorNotifyVisible(false);
    setPendingSubmitPayload(null);
    setSubmittedSubmissionId(null);
  };

  // ── Cancel ────────────────────────────────────────────────────────────────
  const handleCancel = () => {
    if (isDirty) {
      setCancelConfirmVisible(true);
    } else {
      onNavigate('RouteRoadblocks');
    }
  };

  // ── Route unavailable guard ───────────────────────────────────────────────
  if (!routeId) {
    return (
      <View style={styles.errorBanner}>
        <MaterialIcons name="error-outline" size={20} color={AdminColors.red500} />
        <Text style={styles.errorBannerText}>
          {t('route_issue_submit.error_route_unavailable')}
        </Text>
        <TouchableOpacity onPress={() => onNavigate('RouteRoadblocks')}>
          <Text style={styles.returnLink}>{t('route_issue_submit.action_return')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  // ── Loading / Error states ────────────────────────────────────────────────
  if (loading) {
    return (
      <View style={styles.loadingWrap}>
        <ActivityIndicator size="large" color={AdminColors.blue700} />
      </View>
    );
  }

  if (loadError) {
    return (
      <View style={styles.errorBanner}>
        <MaterialIcons name="error-outline" size={20} color={AdminColors.red500} />
        <Text style={styles.errorBannerText}>{loadError}</Text>
        <TouchableOpacity onPress={() => onNavigate('RouteRoadblocks')}>
          <Text style={styles.returnLink}>{t('route_issue_submit.action_return')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView style={styles.root} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      {/* ── Cancel confirmation dialog ────────────────────────────────────────── */}
      <Modal
        visible={cancelConfirmVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setCancelConfirmVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>{t('route_issue_submit.modal_unsaved_title')}</Text>
            <Text style={styles.modalBody}>
              {t('route_issue_submit.modal_unsaved_body')}
            </Text>
            <View style={styles.modalActions}>
              <TouchableOpacity
                style={styles.modalSecondaryBtn}
                onPress={() => setCancelConfirmVisible(false)}
              >
                <Text style={styles.modalSecondaryBtnText}>{t('route_issue_submit.modal_action_stay')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.modalBtn}
                onPress={() => {
                  setCancelConfirmVisible(false);
                  onNavigate('RouteRoadblocks');
                }}
              >
                <Text style={styles.modalBtnText}>{t('route_issue_submit.modal_action_leave')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* ── Supervisor warning modal (step 1): "Have you contacted your DDL?" ── */}
      <Modal
        visible={supervisorWarningVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setSupervisorWarningVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>{t('route_issue_submit.supervisor_warning_modal_title')}</Text>
            <Text style={styles.modalBody}>{t('route_issue_submit.supervisor_warning_modal_body')}</Text>
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.modalSecondaryBtn} onPress={handleSupervisorWarningNo}>
                <Text style={styles.modalSecondaryBtnText}>{t('route_issue_submit.supervisor_warning_modal_no')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.modalBtn} onPress={handleSupervisorWarningYes}>
                <Text style={styles.modalBtnText}>{t('route_issue_submit.supervisor_warning_modal_yes')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* ── Supervisor warning modal (step 2): "Are you sure?" ── */}
      <Modal
        visible={supervisorConfirmVisible}
        transparent
        animationType="fade"
        onRequestClose={handleSupervisorConfirmBack}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>{t('route_issue_submit.supervisor_warning_confirm_title')}</Text>
            <Text style={styles.modalBody}>{t('route_issue_submit.supervisor_warning_confirm_body')}</Text>
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.modalSecondaryBtn} onPress={handleSupervisorConfirmBack}>
                <Text style={styles.modalSecondaryBtnText}>{t('route_issue_submit.supervisor_warning_confirm_back')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.modalBtn} onPress={handleSupervisorConfirmProceed}>
                <Text style={styles.modalBtnText}>{t('route_issue_submit.supervisor_warning_confirm_proceed')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* ── Supervisor automatic notification modal ───────────────────────────── */}
      {supervisorNotifyVisible && submittedSubmissionId !== null && (
        <NotifySupervisorModal
          recordType="Roadblock"
          recordId={submittedSubmissionId}
          onClose={handleSupervisorNotifyClose}
          onBack={handleSupervisorNotifyBack}
        />
      )}

      <ScrollView
        style={styles.scrollRoot}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        {/* ── Breadcrumb ─────────────────────────────────────────────────────── */}
        <View style={styles.breadcrumb}>
          <TouchableOpacity onPress={() => onNavigate('Harbor')}>
            <Text style={styles.bcLink}>{t('breadcrumb_harbor')}</Text>
          </TouchableOpacity>
          <Text style={styles.bcSep}>{' › '}</Text>
          <TouchableOpacity onPress={() => onNavigate('RouteRoadblocks')}>
            <Text style={styles.bcLink}>{t('route_roadblocks.page_title')}</Text>
          </TouchableOpacity>
          <Text style={styles.bcSep}>{' › '}</Text>
          <Text style={styles.bcCurrent}>{t('route_issue_submit.page_title')}</Text>
        </View>

        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Text style={styles.pageTitle}>{t('route_issue_submit.page_title')}</Text>
          <PTLanguageToggle compact={isMobile} />
        </View>

        {/* ── Route info box ────────────────────────────────────────────────── */}
        <View style={styles.infoBox}>
          <View style={styles.roLabel}>
            <Text style={styles.roText}>{t('route_issue_submit.label_readonly')}</Text>
          </View>
          <View style={styles.infoGrid}>
            <View style={styles.infoField}>
              <Text style={styles.infoKey}>{t('route_issue_submit.label_route')}</Text>
              <Text style={styles.infoVal}>{routeId}</Text>
            </View>
            <View style={styles.infoField}>
              <Text style={styles.infoKey}>{t('route_issue_submit.label_date')}</Text>
              <Text style={styles.infoVal}>{formatDate(routeDate)}</Text>
            </View>
            {!!routeLocation && (
              <View style={styles.infoField}>
                <Text style={styles.infoKey}>{t('route_issue_submit.label_location')}</Text>
                <Text style={styles.infoVal}>{routeLocation}</Text>
              </View>
            )}
            {deliveryDetail?.locationName && (
              <View style={styles.infoField}>
                <Text style={styles.infoKey}>{t('route_issue_submit.label_location')}</Text>
                <Text style={styles.infoVal}>{deliveryDetail.locationName}</Text>
              </View>
            )}
          </View>
        </View>

        {/* ── Select Reason Code ────────────────────────────────────────────── */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>{t('route_issue_submit.section_select_reason')}</Text>
          <Text style={styles.cardSub}>
            {t('route_issue_submit.section_select_reason_sub')}
          </Text>

          {reasonCodes.length === 0 ? (
            <Text style={styles.emptyText}>
              {t('route_issue_submit.empty_no_reason_codes')}
            </Text>
          ) : (
            <View style={styles.rcGrid}>
              {reasonCodes.map(rc => {
                const isSel = selectedReasonId === rc.id;
                return (
                  <Pressable
                    key={rc.id}
                    style={[styles.rcTile, { width: reasonTileWidth }, isSel && styles.rcTileSel]}
                    onPress={() => handleReasonSelect(rc.id)}
                  >
                    {isSel && (
                      <View style={styles.rcCheck}>
                        <CheckIcon />
                      </View>
                    )}
                    <View style={styles.rcReasonRow}>
                      <View style={styles.rcReasonIconWrap}>
                        <ReasonCodeIcon iconName={rc.iconName} />
                      </View>
                      <Text style={styles.rcLabel}>{rc.routeRoadblock}</Text>
                    </View>
                  </Pressable>
                );
              })}
            </View>
          )}

          {reasonError ? <Text style={styles.errorText}>{reasonError}</Text> : null}

          {/* ── Missing Equipment sub-section ─────────────────────────────── */}
          {isMissingEquipment && (
            <View style={styles.eqSection}>
              <View style={styles.eqSectionHeader}>
                <BoxIcon />
                <Text style={styles.eqSectionTitle}>{t('route_issue_submit.section_select_equipment')}</Text>
              </View>
              <Text style={styles.eqSectionSub}>
                {t('route_issue_submit.section_select_equipment_sub')}
              </Text>
              {equipmentLoading ? (
                <ActivityIndicator size="small" color={AdminColors.blue700} style={{ marginTop: 8 }} />
              ) : equipmentLoadError || equipmentItems.length === 0 ? (
                <Text style={styles.emptyText}>
                  {t('route_issue_submit.empty_no_equipment')}
                </Text>
              ) : (
                <View style={styles.eqGrid}>
                  {equipmentItems.map(item => {
                    const isSel = selectedEquipment.has(item.equipmentName);
                    return (
                      <Pressable
                        key={item.id}
                        style={[styles.eqChk, isSel && styles.eqChkSel]}
                        onPress={() => toggleEquipment(item.equipmentName)}
                      >
                        <View style={[styles.eqBox, isSel && styles.eqBoxSel]}>
                          {isSel && (
                            <Svg width={11} height={11} viewBox="0 0 24 24" fill="none">
                              <Path d="M5 12.5l4.5 4.5L19 7" stroke="#fff" strokeWidth={4} strokeLinecap="round" strokeLinejoin="round" />
                            </Svg>
                          )}
                        </View>
                        <Text style={styles.eqLabel}>{item.equipmentName}</Text>
                      </Pressable>
                    );
                  })}
                </View>
              )}
              {equipmentError ? <Text style={styles.errorText}>{equipmentError}</Text> : null}
            </View>
          )}
        </View>

        {/* ── Photo Attachment — shown only when picture toggle is on ────────── */}
        {/* key forces remount on reason change so internal preview state clears */}
        {showPhoto && (
          <View key={`photo-${selectedReasonId ?? 'none'}`} style={styles.card}>
            <Text style={styles.cardTitle}>
              {photoLabel}
              {photoRequired ? <Text style={styles.requiredAsterisk}> *</Text> : null}
            </Text>
            <Text style={styles.cardSub}>
              {photoRequired
                ? t('route_issue_submit.section_photo_attachment_required')
                : t('route_issue_submit.section_photo_attachment_optional')}
            </Text>
            <HarborPhotoUpload
              label={pictureAttached ? t('route_issue_submit.label_photo_attached') : t('route_issue_submit.label_attach_photo')}
              onUploaded={handlePhotoUploaded}
            />
          </View>
        )}

        {/* ── Additional Details — shown only when text field toggle is on ───── */}
        {showTextField && (
          <View style={styles.card}>
            <Text style={styles.cardTitle}>
              {textFieldLabel}
              {textFieldRequired && (
                <Text style={styles.requiredAsterisk}> *</Text>
              )}
            </Text>
            <Text style={styles.cardSub}>
              {textFieldRequired
                ? t('route_issue_submit.section_additional_details_required')
                : t('route_issue_submit.section_additional_details_optional')}
            </Text>
            <TextInput
              style={[
                styles.textArea,
                textFieldRequired && details.trim().length === 0 && styles.textAreaError,
              ]}
              multiline
              numberOfLines={4}
              value={details}
              onChangeText={v => setDetails(v.slice(0, DETAILS_MAX_LEN))}
              placeholder={
                textFieldRequired
                  ? t('route_issue_submit.placeholder_details_required')
                  : t('route_issue_submit.placeholder_details_optional')
              }
              placeholderTextColor={AdminColors.textMuted}
              textAlignVertical="top"
            />
            <Text style={[styles.charCounter, details.length === DETAILS_MAX_LEN && styles.charCounterWarn]}>
              {details.length}/{DETAILS_MAX_LEN}
            </Text>
            {textFieldRequired && details.trim().length === 0 && (
              <Text style={styles.errorText}>
                {t('route_issue_submit.error_details_required')}
              </Text>
            )}
          </View>
        )}

        {!!submitBannerError && (
          <View style={styles.submitErrorBanner}>
            <WarnIcon />
            <Text style={styles.submitErrorBannerText}>{submitBannerError}</Text>
          </View>
        )}

        {/* ── Form actions ──────────────────────────────────────────────────── */}
        <View style={styles.formActions}>
          <TouchableOpacity style={styles.cancelBtn} onPress={handleCancel} disabled={submitting}>
            <Text style={styles.cancelBtnText}>{t('route_issue_submit.action_cancel')}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.submitBtn, (!isSubmitEnabled || submitting) && styles.submitBtnDisabled]}
            onPress={handleSubmit}
            disabled={!isSubmitEnabled || submitting}
          >
            {submitting
              ? <ActivityIndicator size="small" color="#fff" />
              : <Text style={[styles.submitBtnText, !isSubmitEnabled && styles.submitBtnTextDisabled]}>{t('route_issue_submit.action_submit')}</Text>
            }
          </TouchableOpacity>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

export default RouteIssueSubmitScreen;
